import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { SubscriptionListDTO } from '@telecom/shared/domain/permission/apis/stripe-api';
import { StripeApi, SubscriptionStatus } from '@telecom/shared/domain/permission/apis/stripe-api';
import type { SubscriptionInfoService } from '../subscription-info-service';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

/**
 * 订阅信息服务实现
 */
@injectable()
export class SubscriptionInfoServiceImpl implements SubscriptionInfoService {
    private _cachedSubscription: SubscriptionListDTO | null = null;
    private _loadingPromise: Promise<SubscriptionListDTO | null> | null = null;

    constructor(
        @inject(StripeApi)
        private _stripeApi: StripeApi,
    ) {}

    /**
     * 获取当前有效订阅
     * 如果已有缓存则返回缓存，否则从 API 获取
     */
    public async getCurrentSubscription(): Promise<SubscriptionListDTO | null> {
        // 如果已有缓存，直接返回
        if (this._cachedSubscription) {
            return this._cachedSubscription;
        }

        // 如果正在加载中，返回加载中的 Promise
        if (this._loadingPromise) {
            return this._loadingPromise;
        }

        // 开始加载
        this._loadingPromise = this._fetchSubscription()
            .then(subscription => {
                this._cachedSubscription = subscription;
                this._loadingPromise = null;
                return subscription;
            })
            .catch(error => {
                this._loadingPromise = null;
                throw error;
            });

        return this._loadingPromise;
    }

    private async _fetchSubscription(): Promise<SubscriptionListDTO | null> {
        const isLogin = await isUserLogin();
        if (!isLogin) {
            return null;
        }

        const subscriptions = await this._stripeApi.getSubscriptionList();
        const activeSubscriptions = subscriptions.filter(item =>
            [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(item.subscription_status),
        );

        return activeSubscriptions.length > 0 ? activeSubscriptions[0] : null;
    }
}
